import React, { useMemo } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    Checkbox
} from '@mui/material';
import { useAppSelector } from '../../hooks/reduxHooks';
import DataTableRow from './DataTableRow';
import Loading from '../Loading';
import NoInfo from '../NoInfo';
import styles from './styles.module.css';
import { useIsMobile } from '../../hooks/useIsMobile';

const MainTable = (props: any) => {
    const {
        items,
        children,
        isFetching,
        style,
        headers,
        columnTitles,
        columnWidths,
        noInfoTitle,
        noInfoSubtitle,
        testID,
        noInfoText = 'noItems',
        hideNoDataMessage = false,
        getRowDisabled,
        noInfoImage,
        columnTitlesFontSize,
        // Opcional: devuelve contenido para una fila extra, a todo el ancho de
        // la tabla, renderizada inmediatamente debajo de la fila del item.
        // Pensada para banners contextuales (p. ej. el motivo de rechazo SVCC
        // en Presentaciones). Si devuelve algo falsy, no se renderiza nada.
        renderRowExtra
    } = props;
    const { isMobile } = useIsMobile();
    const { loading } = useAppSelector((state) => state.application);
    const { legendConfig, legendField, legendResolver } = props;

    const legendMap = useMemo(() => {
        if (!legendConfig) return new Map();

        return new Map(legendConfig.map((item: any) => [item.name, item.color]));
    }, [legendConfig]);

    return (
        <Box sx={style || null}>
            <Paper>
                <TableContainer
                    sx={{
                        maxHeight: '746px',
                        width: '100%',
                        overflowY: 'auto',
                        overflowX: 'auto'
                    }}
                >
                    <Table
                        data-testid={testID || 'table'}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                        className={styles['datatable']}
                        stickyHeader
                        sx={{
                            width: '100%'
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                {columnTitles.map((col: any, index: number) => {
                                    const isLastColumn = index === columnTitles.length - 1;
                                    const isCentered = headers?.[index]?.centerColumn;
                                    const isRightAligned = headers?.[index]?.rightColumn;
                                    // Algunas grillas (p. ej. Visitas) piden que solo esta
                                    // columna se achique en mobile, sin afectar al resto de
                                    // las tablas que comparten este componente.
                                    const isCompactOnMobile =
                                        isMobile && headers?.[index]?.compactOnMobile;

                                    return (
                                        <TableCell
                                            key={index}
                                            style={{
                                                width:
                                                    headers?.[index]?.width ||
                                                    columnWidths?.[index] ||
                                                    'auto',
                                                paddingTop: isCompactOnMobile ? '0.5rem' : '1rem',
                                                paddingBottom: isCompactOnMobile
                                                    ? '0.5rem'
                                                    : '1rem',
                                                fontSize: isCompactOnMobile
                                                    ? '0.75rem'
                                                    : columnTitlesFontSize || '1rem',
                                                paddingLeft: isCompactOnMobile
                                                    ? '0.35rem'
                                                    : isCentered
                                                      ? '0'
                                                      : '.7rem',
                                                paddingRight: isCompactOnMobile
                                                    ? '0.35rem'
                                                    : isCentered
                                                      ? '0'
                                                      : '.7rem',
                                                textAlign: isCentered
                                                    ? 'center'
                                                    : isRightAligned
                                                      ? 'right'
                                                      : undefined,
                                                // stickyHeader (prop del Table de MUI) ya deja
                                                // fijo (position: sticky; top: 0) el header de
                                                // TODAS las columnas al scrollear verticalmente;
                                                // aca solo se suma el sticky horizontal (right:0)
                                                // para la ultima columna en mobile, sin pisar el
                                                // position del resto de los headers.
                                                position:
                                                    isMobile && isLastColumn
                                                        ? 'sticky'
                                                        : undefined,
                                                right: isMobile && isLastColumn ? 0 : undefined,
                                                zIndex: isMobile && isLastColumn ? 2 : undefined
                                            }}
                                        >
                                            {typeof col === 'string' ||
                                            React.isValidElement(col) ? (
                                                col
                                            ) : (
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent={
                                                        isCentered
                                                            ? 'center'
                                                            : isRightAligned
                                                              ? 'flex-end'
                                                              : undefined
                                                    }
                                                    gap={0.5}
                                                >
                                                    {col?.showSelectAllCheck && (
                                                        <Checkbox
                                                            size="small"
                                                            checked={col?.checked ?? false}
                                                            onChange={(e) =>
                                                                col?.onSelectAllCheckPress?.(
                                                                    e.target.checked
                                                                )
                                                            }
                                                            style={{ color: 'white' }}
                                                        />
                                                    )}
                                                    {col?.label}
                                                </Box>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody className={styles['datatable-tbody']}>
                            {isFetching || loading ? (
                                <TableRow>
                                    <TableCell colSpan={headers?.length}>
                                        <Loading size="small" />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items?.map((item: any, index: number) => {
                                    const legendValue = legendResolver
                                        ? legendResolver(item)
                                        : legendField
                                          ? item?.[legendField]
                                          : undefined;
                                    const legendColor = legendValue
                                        ? legendMap.get(legendValue)
                                        : undefined;

                                    const rowExtra = renderRowExtra?.(item);

                                    return (
                                        <React.Fragment key={item?.id || index}>
                                            <DataTableRow
                                                columnWidths={columnWidths}
                                                item={item}
                                                headers={headers}
                                                isMobile={isMobile}
                                                getRowDisabled={getRowDisabled}
                                                legendColor={legendColor}
                                                legendResolver={legendResolver}
                                                {...props}
                                            />
                                            {rowExtra && (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={
                                                            headers?.length ||
                                                            columnTitles?.length ||
                                                            1
                                                        }
                                                        sx={{ py: 0, borderBottom: 'none' }}
                                                    >
                                                        {rowExtra}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                    {items === undefined && !loading && !isFetching && !hideNoDataMessage && (
                        <NoInfo title={noInfoTitle} subtitle={noInfoSubtitle} image={noInfoImage} />
                    )}
                    {items?.length < 1 && !loading && !isFetching && !hideNoDataMessage && (
                        <NoInfo
                            text={noInfoText}
                            title={noInfoTitle}
                            subtitle={noInfoSubtitle}
                            image={noInfoImage}
                        />
                    )}
                </TableContainer>
            </Paper>
            {children && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 16px',
                        flexWrap: 'wrap',
                        gap: 2
                    }}
                >
                    {children}
                </Box>
            )}
        </Box>
    );
};

export default MainTable;
